import React, { useState, useEffect } from 'react';
import { Clock, Car, DollarSign, Calendar, Percent, ChevronRight, Loader2, GitCompare, Trash2 } from 'lucide-react';
import { getUserLeases, deleteLease } from '../services/leaseService';
import type { LeaseRecord } from '../types/lease';
import { moneyFactorToAPR } from '../utils/leaseCalculator';

interface LeaseHistoryProps {
  userId: string;
  onSelectLease: (lease: LeaseRecord) => void;
}

export function LeaseHistory({ userId, onSelectLease }: LeaseHistoryProps) {
  const [leases, setLeases] = useState<LeaseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    const fetchLeases = async () => {
      try {
        const userLeases = await getUserLeases(userId);
        setLeases(userLeases.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()));
      } catch (err) {
        setError('Failed to load lease history');
        console.error('Error fetching leases:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeases();
  }, [userId]);

  const handleDelete = async (leaseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this lease analysis?')) {
      return;
    }

    setDeleting(leaseId);
    try {
      await deleteLease(leaseId, userId);
      setLeases(leases.filter(lease => lease.id !== leaseId));
    } catch (err) {
      console.error('Error deleting lease:', err);
      alert('Failed to delete lease analysis');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (leases.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No lease analyses yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {leases.map((lease) => (
        <div
          key={lease.id}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-blue-200 transition-colors group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-50 p-2 rounded-lg group-hover:bg-blue-100 transition-colors">
                <Car className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">
                  {new Date(lease.createdAt.toMillis()).toLocaleDateString()}
                </div>
                <div className="font-medium text-gray-900">
                  ${lease.sellingPrice.toLocaleString()}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">Monthly Payment</div>
                <div className="font-medium text-gray-900">
                  ${lease.monthlyPayment.toFixed(2)}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => onSelectLease(lease)}
                  className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                  title="Load this lease analysis for viewing or editing"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectLease(lease);
                  }}
                  className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                  title="Compare this lease with your current analysis"
                >
                  <GitCompare className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => handleDelete(lease.id, e)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete this lease analysis permanently"
                  disabled={deleting === lease.id}
                >
                  {deleting === lease.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
            <div title="Length of the lease agreement">
              <div className="text-gray-500 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Term
              </div>
              <div className="font-medium text-gray-700">{lease.term} months</div>
            </div>
            <div title="Annual Percentage Rate calculated from money factor">
              <div className="text-gray-500 flex items-center">
                <Percent className="w-4 h-4 mr-1" />
                APR
              </div>
              <div className="font-medium text-gray-700">
                {moneyFactorToAPR(lease.moneyFactor).toFixed(2)}%
              </div>
            </div>
            <div title="Total cost including all payments and fees over the lease term">
              <div className="text-gray-500 flex items-center">
                <DollarSign className="w-4 h-4 mr-1" />
                Total Cost
              </div>
              <div className="font-medium text-gray-700">
                ${lease.totalCost.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}